import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

/**
 * D3StackedBar - Reusable stacked bar chart component
 * Updated:
 * - All bars are strict rectangles (no rounded corners).
 * - Values formatted with thousand separators and "K" (e.g. 1,500K).
 */
function D3StackedBar({
  data,
  categories,
  colorPalette,
  currencySymbol = "",
  options = {},
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const lastDimensions = useRef({ width: 0, height: 0 });
  const typography = useRccTypography();

  // Helper: Format large numbers with commas and K (e.g. 1500000 -> 1,500K)
  const kFormatter = (v) => {
    if (v === 0) return "0";
    if (Math.abs(v) < 1000) return `${currencySymbol}${d3.format(",")(v)}`;
    return `${currencySymbol}${d3.format(",.0f")(v / 1000)}K`;
  };

  // Helper: Format large numbers in millions (e.g. 1500000 -> 1.5M)
  const mFormatter = (v) => {
    if (v === 0) return "0";
    if (Math.abs(v) < 1000) return `${currencySymbol}${d3.format(",")(v)}`;
    return `${currencySymbol}${d3.format(",.1f")(v / 1_000_000)}M`;
  };

  const {
    yAxisLabel = "",
    xAxisLabel = "",
    yAxisFormatter = kFormatter,
    valueFormatter = kFormatter,
    autoScaleMillions = true,
    showLabels = true,
    showLegend = true,
    marginTop = 10,
    marginRight = 10,
    marginBottom = 40,
    marginLeft = 50, // Slightly increased to fit larger labels
    barPadding = 0.2,
    minLabelHeight = 20,
    maxBarWidth = 72,
  } = options;

  // Helper: Build color map from the paired palette
  const getColorMap = () => {
    const map = {};

    // We assume colorPalette is always an array of { color, textColor } objects
    if (Array.isArray(colorPalette) && colorPalette.length > 0) {
      categories.forEach((cat, idx) => {
        // Cycle through the fixed palette
        const paletteItem = colorPalette[idx % colorPalette.length];

        // Directly assign the pair
        map[cat] = {
          bg: paletteItem.color,
          text: paletteItem.textColor,
        };
      });
    } else {
      // Fallback only if the prop is missing or empty
      categories.forEach((cat) => {
        map[cat] = { bg: rccColors.neutral[400], text: "#ffffff" };
      });
    }
    return map;
  };

  const colorMap = getColorMap();

  useEffect(() => {
    if (
      !wrapperRef.current ||
      !containerRef.current ||
      !data?.length ||
      !categories?.length
    )
      return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) return;

      // Get dimensions from wrapper
      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const margin = {
        top: marginTop,
        right: marginRight,
        bottom: marginBottom,
        left: marginLeft,
      };
      const width = containerWidth - margin.left - margin.right;
      const chartHeight = containerHeight - margin.top - margin.bottom;

      // Skip if dimensions are invalid
      if (width <= 0 || chartHeight <= 0) return;

      // Skip if dimensions haven't changed (prevents resize loop)
      const { width: lastW, height: lastH } = lastDimensions.current;
      if (containerWidth === lastW && containerHeight === lastH) return;

      // Store current dimensions before drawing
      lastDimensions.current = {
        width: containerWidth,
        height: containerHeight,
      };

      // Clear previous SVG
      d3.select(container).select("svg").remove();

      const svgElement = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .style("display", "block")
        .style("margin", 0)
        .style("padding", 0);

      const svg = svgElement
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Prepare data for stacked bar chart
      const stack = d3.stack().keys(categories);
      const stackedData = stack(data);

      // --- SCALES & MAX WIDTH LOGIC ---
      const xScale = d3
        .scaleBand()
        .domain(data.map((d) => d.xAxisLabel || d.quarter || d.category || ""))
        .range([0, width])
        .padding(barPadding);

      if (maxBarWidth && xScale.bandwidth() > maxBarWidth) {
        const step = xScale.step();
        const newPadding = 1 - maxBarWidth / step;
        xScale.padding(Math.max(barPadding, newPadding));
      }

      const maxValue = d3.max(data, (d) =>
        categories.reduce((sum, key) => sum + (d[key] || 0), 0)
      );

      // Auto-scale to millions if values exceed or equal 1,000,000
      const useMillions = autoScaleMillions && maxValue >= 1_000_000;
      const yFmt = useMillions ? mFormatter : yAxisFormatter;
      const segmentFmt = useMillions ? mFormatter : valueFormatter;
      const totalFmt = useMillions ? mFormatter : valueFormatter;

      const yScale = d3
        .scaleLinear()
        .domain([0, maxValue * 1.1])
        .nice()
        .range([chartHeight, 0]);

      // Y-axis
      svg
        .append("g")
        .call(d3.axisLeft(yScale).tickFormat(yFmt).ticks(8))
        .call((g) => g.select(".domain").attr("stroke", rccColors.neutral[100]))
        .call((g) =>
          g.selectAll(".tick line").attr("stroke", rccColors.neutral[100])
        )
        .call((g) =>
          g
            .selectAll(".tick text")
            .attr("class", typography.textLabelRegular)
            .style("fill", rccColors.neutral[800])
        );

      // X-axis
      svg
        .append("g")
        .attr("transform", `translate(0,${chartHeight})`)
        .call(d3.axisBottom(xScale).tickSizeInner(6).tickSizeOuter(0))
        .call((g) => g.select(".domain").attr("stroke", rccColors.neutral[100]))
        .call((g) =>
          g.selectAll(".tick line").attr("stroke", rccColors.neutral[100])
        )
        .call((g) =>
          g
            .selectAll(".tick text")
            .attr("class", typography.textExtraSmallRegular)
            .style("fill", rccColors.neutral[800])
        );

      // Calculate totals for each bar for percentage calculation
      const barTotals = data.map((d) =>
        categories.reduce((sum, key) => sum + (d[key] || 0), 0)
      );

      const barWidth = xScale.bandwidth();

      stackedData.forEach((layerData) => {
        const category = layerData.key;

        // --- Extract colors from our map ---
        const colors = colorMap[category] || {
          bg: rccColors.neutral[400],
          text: "#fff",
        };
        const barColor = colors.bg;
        const textColor = colors.text;

        layerData.forEach((d) => {
          const x = xScale(
            d.data.xAxisLabel || d.data.quarter || d.data.category || ""
          );
          const y = yScale(d[1]);
          const segmentHeight = yScale(d[0]) - yScale(d[1]);
          const value = d[1] - d[0];

          if (segmentHeight > 0 && value > 0) {
            // Draw simple rectangle - No borders, No rounded corners
            svg
              .append("rect")
              .attr("x", x)
              .attr("y", y)
              .attr("height", segmentHeight)
              .attr("width", barWidth)
              .attr("fill", barColor);

            // Add labels inside bars
            if (
              showLabels &&
              segmentHeight >= minLabelHeight &&
              barWidth > 40
            ) {
              const barIndex = data.findIndex(
                (item) =>
                  (item.xAxisLabel || item.quarter || item.category) ===
                  (d.data.xAxisLabel || d.data.quarter || d.data.category)
              );
              const total = barTotals[barIndex];
              const percentage =
                total > 0 ? Math.round((value / total) * 100) : 0;

              // Only show labels if segment is significant
              const minSignificantHeight = Math.max(25, chartHeight * 0.03);
              if (segmentHeight < minSignificantHeight && percentage < 3) {
                return;
              }

              const labelText = `${segmentFmt(value)} (${percentage}%)`;
              svg
                .append("text")
                .attr("x", x + barWidth / 2)
                .attr("y", y + segmentHeight / 2)
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
                .attr("class", typography.textLabelSemibold)
                .style("fill", textColor)
                .text(labelText);
            }
          }
        });
      });

      // Add total labels on top of bars
      data.forEach((d, index) => {
        const total = barTotals[index];
        const x = xScale(d.xAxisLabel || d.quarter || d.category || "");
        const topY = yScale(total);

        if (topY > 25 && barWidth > 40 && total > 0) {
          svg
            .append("text")
            .attr("x", x + barWidth / 2)
            .attr("y", topY - 8)
            .attr("text-anchor", "middle")
            .attr("class", typography.textExtraSmallSemibold)
            .style("fill", rccColors.neutral[800])
            .text(totalFmt(total));
        }
      });
    };

    renderChart();

    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(renderChart, 50);
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(wrapperRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [
    data,
    categories,
    colorPalette,
    options,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    barPadding,
    minLabelHeight,
    maxBarWidth,
    showLabels,
    showLegend,
    yAxisFormatter,
    valueFormatter,
    colorMap,
  ]);

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
          minHeight: 0,
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
            margin: 0,
            padding: 0,
          }}
        />
      </div>
      {showLegend && (
        <div
          style={{
            // 1. OUTER CONTAINER: Defines the available space and centers the inner block
            display: "flex",
            justifyContent: "center", // This centers the "Inner Block"
            width: "100%",
            paddingTop: "16px",

            // Apply chart margins so the center point matches the chart's visual center
            paddingLeft: `${marginLeft}px`,
            paddingRight: `${marginRight}px`,
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              // 2. INNER BLOCK: Tries to be only as wide as the content
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              gap: "8px 32px",
              maxWidth: "100%", // Ensures it doesn't overflow
            }}
          >
            {categories.map((category) => (
              <div
                key={category}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <div
                  style={{
                    width: "6px",
                    height: "6px",
                    borderRadius: "50%",
                    backgroundColor:
                      colorMap[category]?.bg || rccColors.neutral[400],
                    flexShrink: 0,
                  }}
                />
                <span
                  className={typography.textSmallRegular}
                  style={{
                    color: rccColors.neutral[800],
                    whiteSpace: "nowrap",
                  }}
                >
                  {category}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default D3StackedBar;
